/**
 * SuppliersContent - Content renderer for Suppliers panel
 * 
 * Displays supplier list and Approved Vendor List (AVL).
 * Supports supplier approval workflow.
 */

import { useState } from 'react';
import { Button } from '@/components/ui';

type SupplierTab = 'suppliers' | 'avl';

interface Supplier {
  supplierId: string;
  name: string;
  code: string;
  status: 'active' | 'inactive';
  approvalStatus: 'pending' | 'approved' | 'rejected';
  certifications?: string[];
  categories?: string[];
  contactInfo?: {
    email?: string;
    phone?: string;
    address?: string;
  };
  rating?: number;
}

interface SuppliersContentProps {
  suppliers: Supplier[];
  avl: Supplier[];
  loading: boolean;
  isSearching: boolean;
  searchQuery: string;
  onSupplierClick: (supplier: Supplier) => void;
  onApprove: (supplierId: string) => void;
  onReject: (supplierId: string) => void;
  onCreateSupplier: () => void;
}

export function SuppliersContent({
  suppliers,
  avl,
  loading,
  isSearching,
  searchQuery,
  onSupplierClick,
  onApprove,
  onReject,
  onCreateSupplier,
}: SuppliersContentProps) {
  const [activeTab, setActiveTab] = useState<SupplierTab>('suppliers');
  
  // Loading state
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3 mx-auto"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // Searching state
  if (isSearching) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin text-3xl mb-3">🔍</div>
        <p className="text-muted-foreground">Searching suppliers...</p>
      </div>
    );
  }
  
  // Filter by search query
  const filterSuppliers = (list: Supplier[]) => {
    if (!searchQuery.trim()) return list;
    const query = searchQuery.toLowerCase();
    return list.filter(s => 
      s.name.toLowerCase().includes(query) ||
      s.code.toLowerCase().includes(query) ||
      s.contactInfo?.email?.toLowerCase().includes(query) ||
      s.certifications?.some(c => c.toLowerCase().includes(query))
    );
  };
  
  const filteredSuppliers = filterSuppliers(suppliers);
  const filteredAVL = filterSuppliers(avl);
  
  const currentList = activeTab === 'suppliers' ? filteredSuppliers : filteredAVL;
  
  // Empty state
  if (suppliers.length === 0 && avl.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-5xl mb-4">🏭</div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          No suppliers yet
        </h3>
        <p className="text-muted-foreground mb-4">
          Add your first supplier to start managing your supply chain
        </p>
        <Button onClick={onCreateSupplier}>
          + New Supplier
        </Button>
      </div>
    );
  }
  
  // Group suppliers by approval status
  const pendingSuppliers = filteredSuppliers.filter(s => s.approvalStatus === 'pending');
  const approvedSuppliers = filteredSuppliers.filter(s => s.approvalStatus === 'approved');
  const rejectedSuppliers = filteredSuppliers.filter(s => s.approvalStatus === 'rejected');
  
  return (
    <div className="flex flex-col h-full">
      {/* Tabs */}
      <div className="flex-shrink-0 px-4">
        <div className="flex gap-4">
          <Button
            variant="ghost"
            onClick={() => setActiveTab('suppliers')}
            className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors rounded-none ${
              activeTab === 'suppliers'
                ? 'border-primary text-info'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            All Suppliers ({filteredSuppliers.length})
          </Button>
          <Button
            variant="ghost"
            onClick={() => setActiveTab('avl')}
            className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors rounded-none ${
              activeTab === 'avl'
                ? 'border-primary text-info'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            AVL ({filteredAVL.length})
          </Button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {activeTab === 'suppliers' ? (
          <>
            {/* Pending Approval */}
            {pendingSuppliers.length > 0 && (
              <SupplierGroup
                label="Pending Approval"
                icon="⏳"
                suppliers={pendingSuppliers}
                onSupplierClick={onSupplierClick}
                onApprove={onApprove}
                onReject={onReject}
                showActions={true}
                defaultExpanded={true}
              />
            )}
            
            {/* Approved */}
            {approvedSuppliers.length > 0 && (
              <SupplierGroup
                label="Approved"
                icon="✓"
                suppliers={approvedSuppliers}
                onSupplierClick={onSupplierClick}
                showActions={false}
                defaultExpanded={pendingSuppliers.length === 0}
              />
            )}
            
            {/* Rejected */}
            {rejectedSuppliers.length > 0 && (
              <SupplierGroup
                label="Rejected"
                icon="✗"
                suppliers={rejectedSuppliers}
                onSupplierClick={onSupplierClick}
                showActions={false}
                defaultExpanded={false}
              />
            )}
            
            {currentList.length === 0 && searchQuery.trim() && (
              <div className="p-8 text-center">
                <div className="text-4xl mb-3">📭</div>
                <p className="text-muted-foreground">
                  No suppliers match "{searchQuery}"
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            {/* AVL List */}
            {filteredAVL.length > 0 ? (
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="bg-muted/30 px-4 py-2">
                  <h3 className="font-semibold text-foreground">
                    Approved Vendor List (AVL)
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    AS9100D & AS9115A compliant supplier approval tracking
                  </p>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-slate-700">
                  {filteredAVL.map(supplier => (
                    <AVLCard
                      key={supplier.supplierId}
                      supplier={supplier}
                      onClick={() => onSupplierClick(supplier)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="text-4xl mb-3">📭</div>
                <p className="text-muted-foreground">
                  {searchQuery.trim() 
                    ? `No AVL suppliers match "${searchQuery}"`
                    : 'No approved suppliers yet'
                  }
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Supplier group component
interface SupplierGroupProps {
  label: string;
  icon: string;
  suppliers: Supplier[];
  onSupplierClick: (supplier: Supplier) => void;
  onApprove?: (supplierId: string) => void;
  onReject?: (supplierId: string) => void;
  showActions: boolean;
  defaultExpanded: boolean;
}

function SupplierGroup({
  label,
  icon,
  suppliers,
  onSupplierClick,
  onApprove,
  onReject,
  showActions,
  defaultExpanded,
}: SupplierGroupProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Group header */}
      <Button
        variant="ghost"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-2 bg-muted/30 hover:bg-muted/50 transition-colors rounded-none h-auto"
      >
        <div className="flex items-center gap-2">
          <span>{icon}</span>
          <span className="font-medium text-foreground">
            {label}
          </span>
          <span className="text-sm text-muted-foreground">
            ({suppliers.length})
          </span>
        </div>
        <span className="text-muted-foreground">
          {isExpanded ? '▼' : '▶'}
        </span>
      </Button>
      
      {/* Suppliers */}
      {isExpanded && (
        <div className="divide-y divide-gray-100 dark:divide-slate-700">
          {suppliers.map(supplier => (
            <SupplierCard
              key={supplier.supplierId}
              supplier={supplier}
              onClick={() => onSupplierClick(supplier)}
              onApprove={showActions && onApprove ? () => onApprove(supplier.supplierId) : undefined}
              onReject={showActions && onReject ? () => onReject(supplier.supplierId) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Supplier card component
interface SupplierCardProps {
  supplier: Supplier;
  onClick: () => void;
  onApprove?: () => void;
  onReject?: () => void;
}

function SupplierCard({ supplier, onClick, onApprove, onReject }: SupplierCardProps) {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-success-light text-success';
      case 'inactive':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };
  
  const getApprovalBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-success-light text-success';
      case 'pending':
        return 'bg-warning-light text-warning';
      case 'rejected':
        return 'bg-destructive-light text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };
  
  return (
    <div
      className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono text-muted-foreground">
              {supplier.code}
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded ${getStatusBadge(supplier.status)}`}>
              {supplier.status}
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded ${getApprovalBadge(supplier.approvalStatus)}`}>
              {supplier.approvalStatus}
            </span>
          </div>
          <h4 className="font-medium text-foreground">
            {supplier.name}
          </h4>
        </div>
      </div>
      
      {/* Certifications */}
      {supplier.certifications && supplier.certifications.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {supplier.certifications.map(cert => (
            <span
              key={cert}
              className="text-xs px-1.5 py-0.5 bg-info-light text-info rounded"
            >
              {cert}
            </span>
          ))}
        </div>
      )}
      
      {/* Contact info */}
      {supplier.contactInfo?.email && (
        <div className="text-xs text-muted-foreground">
          {supplier.contactInfo.email}
        </div>
      )}
      
      {/* Action buttons for pending approval */}
      {(onApprove || onReject) && (
        <div className="flex gap-2 mt-3" onClick={e => e.stopPropagation()}>
          {onApprove && (
            <Button
              onClick={onApprove}
              variant="success"
              size="xs"
            >
              Approve
            </Button>
          )}
          {onReject && (
            <Button
              onClick={onReject}
              variant="destructive"
              size="xs"
            >
              Reject
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// AVL card component
interface AVLCardProps {
  supplier: Supplier;
  onClick: () => void;
}

function AVLCard({ supplier, onClick }: AVLCardProps) {
  return (
    <div
      className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono text-muted-foreground">
              {supplier.code}
            </span>
            {supplier.rating !== undefined && supplier.rating > 0 && (
              <span className="text-xs text-warning">
                {'⭐'.repeat(Math.min(supplier.rating, 5))}
              </span>
            )}
          </div>
          <h4 className="font-medium text-foreground">
            {supplier.name}
          </h4>
        </div>
      </div>
      
      {/* Certifications */}
      {supplier.certifications && supplier.certifications.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {supplier.certifications.map(cert => (
            <span
              key={cert}
              className="text-xs px-1.5 py-0.5 bg-success-light text-success rounded"
            >
              {cert}
            </span>
          ))}
        </div>
      )}
      
      {/* Categories */}
      {supplier.categories && supplier.categories.length > 0 && (
        <div className="text-xs text-muted-foreground">
          {supplier.categories.join(', ')}
        </div>
      )}
    </div>
  );
}

